import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive } from "@/pages/Senders";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Play, Pause, Stop, Trash } from "@phosphor-icons/react";
import CampaignBuilder from "@/pages/CampaignBuilder";

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [building, setBuilding] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const r = await api.get("/campaigns");
    setCampaigns(r.data.campaigns);
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, []);

  const ctrl = async (id, action) => {
    try {
      await api.post(`/campaigns/${id}/${action}`);
      toast.success(`Campaign ${action}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete campaign?")) return;
    await api.delete(`/campaigns/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="micro mb-1">Operations</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">Campaigns</h1>
        </div>
        <BtnPrimary onClick={() => setBuilding(true)} testid="campaigns-new-btn">
          <Plus size={14} weight="bold" /> New campaign
        </BtnPrimary>
      </div>

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
        <table className="dtable w-full" data-testid="campaigns-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th className="num">Recipients</th>
              <th className="num">Processed</th>
              <th className="num">Accepted</th>
              <th className="num">Failed</th>
              <th className="num">Proxy fail</th>
              <th>Batch</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && (
              <tr><td colSpan={10} className="text-center text-[#71717A] py-8 font-mono">[ no campaigns yet ]</td></tr>
            )}
            {campaigns.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/campaigns/${c.id}`} className="font-medium hover:text-[#E1FF00]" data-testid={`c-link-${c.id}`}>
                    {c.name}
                  </Link>
                </td>
                <td><StatusBadge status={c.status} /></td>
                <td className="num data-num" data-testid={`c-recipients-${c.id}`}>{c.total ?? 0}</td>
                <td className="num data-num">{c.total_processed || 0}</td>
                <td className="num data-num text-[#00FF66]">{c.total_accepted || 0}</td>
                <td className="num data-num text-[#FF3B30]">{c.total_failed || 0}</td>
                <td className="num data-num text-[#FF9500]">{c.total_proxy_failed || 0}</td>
                <td className="font-mono text-xs">B{c.current_batch_number || 1}</td>
                <td className="font-mono text-xs text-[#71717A]">{c.created_at?.slice(0, 19)}</td>
                <td>
                  <div className="flex gap-1">
                    {c.status === "draft" && (
                      <IconBtn testid={`c-start-${c.id}`} onClick={() => ctrl(c.id, "start")}><Play size={12} weight="fill" /></IconBtn>
                    )}
                    {c.status === "running" && (
                      <IconBtn testid={`c-pause-${c.id}`} onClick={() => ctrl(c.id, "pause")}><Pause size={12} weight="fill" /></IconBtn>
                    )}
                    {c.status === "paused" && (
                      <IconBtn testid={`c-resume-${c.id}`} onClick={() => ctrl(c.id, "resume")}><Play size={12} weight="fill" /></IconBtn>
                    )}
                    {(c.status === "running" || c.status === "paused") && (
                      <IconBtn testid={`c-stop-${c.id}`} onClick={() => ctrl(c.id, "stop")}><Stop size={12} weight="fill" /></IconBtn>
                    )}
                    <IconBtn testid={`c-del-${c.id}`} onClick={() => del(c.id)}><Trash size={12} weight="bold" /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {building && (
        <CampaignBuilder onClose={() => { setBuilding(false); load(); }} onCreated={(id) => nav(`/campaigns/${id}`)} />
      )}
    </div>
  );
}

function IconBtn({ children, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="w-7 h-7 flex items-center justify-center border border-[#2A2A2A] hover:border-[#E1FF00] hover:text-[#E1FF00] text-[#A1A1AA] transition-colors">
      {children}
    </button>
  );
}
